import { Link } from "react-router-dom";
import type { Leverage, ParamSpec } from "@/api/types";
import { resolveScriptLeverage } from "@/lib/strategy-leverage";

/** Exchange symbols are usually bare (BTCUSDT); strategies often use BTCUSDT.P. */
export function exchangeSymbolKey(symbol: string): string {
  return String(symbol || "")
    .trim()
    .toUpperCase()
    .replace(/\.P$/i, "");
}

export function findExchangeLeverage(
  leverages: Leverage[] | undefined,
  symbol: string
): Leverage | undefined {
  const key = exchangeSymbolKey(symbol);
  if (!key || !leverages?.length) return undefined;
  return leverages.find((l) => exchangeSymbolKey(l.symbol) === key);
}

type Props = {
  symbol: string;
  strategyConfig?: Record<string, unknown>;
  strategySpecs?: ParamSpec[];
  accountId?: string | null;
  accountLeverages?: Leverage[];
  compact?: boolean;
  className?: string;
};

function fmtLev(n: number): string {
  return `${n}×`;
}

/**
 * One-line leverage check: green when strategy == exchange, amber otherwise.
 */
export function ExchangeLeverageNotice({
  symbol,
  strategyConfig = {},
  strategySpecs = [],
  accountId,
  accountLeverages,
  compact = false,
  className = "",
}: Props) {
  const source = String(strategyConfig.source ?? "");
  const strategyLev = resolveScriptLeverage(strategySpecs, strategyConfig, source);
  const exch = findExchangeLeverage(accountLeverages, symbol);
  const exchangeLev =
    exch != null && Number(exch.leverage) > 0 ? Number(exch.leverage) : undefined;

  const matched =
    strategyLev != null && exchangeLev != null && strategyLev === exchangeLev;
  const mismatch =
    strategyLev != null && exchangeLev != null && strategyLev !== exchangeLev;

  const strat = strategyLev != null ? fmtLev(strategyLev) : "—";
  const live = exchangeLev != null ? fmtLev(exchangeLev) : "—";
  const sym = symbol || "symbol";

  let title: string;
  let hint: string;
  if (matched) {
    title = `Leverage matches: ${strat}`;
    hint = `${sym} on the exchange uses the same leverage as the backtest.`;
  } else if (mismatch) {
    title = `Exchange leverage ${live}, backtest ${strat}`;
    hint = `Set ${sym} to ${strat} on the exchange before the first order.`;
  } else if (strategyLev != null) {
    title = `Set ${sym} to ${strat} on the exchange`;
    hint = "The backtest assumes this leverage.";
  } else {
    title = `Check ${sym} leverage on the exchange`;
    hint = "Set it to what the strategy assumes.";
  }

  const ok = matched;
  return (
    <div
      className={`flex items-start gap-2.5 rounded-[12px] border px-3 ${compact ? "py-2" : "py-2.5"} ${className}`.trim()}
      style={{
        borderColor: `color-mix(in srgb, ${ok ? "var(--success)" : "var(--warning)"} 35%, var(--border))`,
        background: `color-mix(in srgb, ${ok ? "var(--success)" : "var(--warning)"} 8%, transparent)`,
      }}
      role="status"
    >
      <span
        className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px] font-bold"
        style={{
          background: `color-mix(in srgb, ${ok ? "var(--success)" : "var(--warning)"} 16%, transparent)`,
          color: ok ? "var(--success)" : "var(--warning)",
        }}
      >
        {ok ? "✓" : "!"}
      </span>
      <div className="min-w-0 flex-1">
        <b className="block text-[13px] font-medium text-[var(--text-primary)]">{title}</b>
        {!compact && <span className="text-xs text-[var(--text-muted)]">{hint}</span>}
      </div>
      {accountId ? (
        <Link to={`/integrations/marketplaces/${accountId}`} className="shrink-0 self-center text-xs text-[var(--primary)] no-underline">
          Exchange
        </Link>
      ) : null}
    </div>
  );
}
